/**
 * AnswerLane.tsx — W21 Primary Answer Surface
 *
 * Route: /accounts/:id/answer
 *
 * Three-column layout inside OperatorShell:
 *   LEFT RAIL (240px)  — CP1-CP10 picker, gate-state dots, answered/total, percent bar
 *   CENTER (flex)      — One question at a time, typed by responseType, evidence, coach voice
 *   RIGHT RAIL (320px) — composite_score gauge, macro counter, next-gate-at-risk, next-best
 *
 * Tablet (1024-1280): left rail collapses to top horizontal CP strip
 * Mobile (<1024): bottom-sheet for right rail
 *
 * Keyboard:
 *   Tab       — native browser tab
 *   Enter     — save + advance
 *   Cmd+S     — save only
 *   J / down  — next question in CP
 *   K / up    — prev question in CP
 *   Cmd+1..0  — jump to CP1-CP10
 *   Esc       — collapse evidence / close drawers
 *
 * Canon CP names, 5-color palette locked.
 * No emojis. No exclamation points. M-PACT hyphenated.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "wouter";
import OperatorShell from "@/components/operator/OperatorShell";
import CpLeftRail from "@/components/answer-lane/CpLeftRail";
import QuestionCard, { type QuestionCardHandle } from "@/components/answer-lane/QuestionCard";
import ProgressRightRail from "@/components/answer-lane/ProgressRightRail";
import { useAnswerLane, CP_NAMES } from "@/hooks/useAnswerLane";
import type { Question } from "@/hooks/useAnswerLane";

// Responsive breakpoints
const TABLET_BP = 1280;
const MOBILE_BP = 1024;

function useWindowWidth() {
  const [w, setW] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 1440,
  );
  useEffect(() => {
    const handler = () => setW(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return w;
}

export default function AnswerLane() {
  const params = useParams<{ id: string }>();
  const accountId = parseInt(params.id ?? "0", 10);
  const width = useWindowWidth();
  const isTablet = width >= MOBILE_BP && width < TABLET_BP;
  const isMobile = width < MOBILE_BP;

  const {
    account,
    accountLoading,
    cpProgress,
    questions,
    questionsLoading,
    saveAnswer,
    savingKey,
    lastSavedAt,
    getNextUnansweredInCp,
    getFirstUnansweredInNextCp,
    nextBestQuestion,
    totalAnswered,
    totalQuestions,
  } = useAnswerLane(accountId);

  const [activeCp, setActiveCp] = useState(1);
  const [activeQuestion, setActiveQuestion] = useState<Question | null>(null);
  const [bottomSheetOpen, setBottomSheetOpen] = useState(false);
  const [celebrationCp, setCelebrationCp] = useState<number | null>(null);

  const questionCardRef = useRef<QuestionCardHandle>(null);

  // Initialize to first unanswered question
  useEffect(() => {
    if (questions.length === 0) return;
    if (activeQuestion) return; // already set
    const firstUnanswered = questions.find((q) => !q.is_answered);
    if (firstUnanswered) {
      setActiveCp(firstUnanswered.cp_number);
      setActiveQuestion(firstUnanswered);
    } else {
      // All answered — start at CP1 Q1
      const first = questions.find((q) => q.cp_number === 1) ?? questions[0] ?? null;
      if (first) {
        setActiveCp(first.cp_number);
        setActiveQuestion(first);
      }
    }
  }, [questions]);

  // Questions in the active CP
  const cpQuestions = questions.filter((q) => q.cp_number === activeCp);
  const activeIndex = activeQuestion
    ? cpQuestions.findIndex((q) => q.question_key === activeQuestion.question_key)
    : -1;

  function selectCp(cp: number) {
    setActiveCp(cp);
    const first = questions.find((q) => q.cp_number === cp && !q.is_answered)
      ?? questions.find((q) => q.cp_number === cp)
      ?? null;
    setActiveQuestion(first);
  }

  function jumpToQuestion(questionKey: string, cpNumber: number) {
    const q = questions.find((q) => q.question_key === questionKey) ?? null;
    if (q) {
      setActiveCp(cpNumber);
      setActiveQuestion(q);
    }
  }

  const handleAdvance = useCallback(() => {
    if (!activeQuestion) return;

    // Next unanswered in same CP
    const nextInCp = getNextUnansweredInCp(activeCp, activeQuestion.question_key);
    if (nextInCp) {
      setActiveQuestion(nextInCp);
      return;
    }

    // CP is complete — check if we should celebrate
    const cpProg = cpProgress.find((p) => p.cp_number === activeCp);
    if (cpProg && cpProg.answered >= cpProg.total && cpProg.total > 0) {
      setCelebrationCp(activeCp);
      setTimeout(() => setCelebrationCp(null), 2500);
    }

    // Move to first unanswered in next CP
    const next = getFirstUnansweredInNextCp(activeCp);
    if (next) {
      setActiveCp(next.cpNumber);
      setActiveQuestion(next.question);
      return;
    }

    // All done — stay on current question
  }, [activeQuestion, activeCp, cpProgress, getNextUnansweredInCp, getFirstUnansweredInNextCp]);

  const handlePrev = useCallback(() => {
    if (activeIndex > 0) {
      setActiveQuestion(cpQuestions[activeIndex - 1]);
    }
  }, [activeIndex, cpQuestions]);

  const handleNext = useCallback(() => {
    if (activeIndex < cpQuestions.length - 1) {
      setActiveQuestion(cpQuestions[activeIndex + 1]);
    }
  }, [activeIndex, cpQuestions]);

  // Keyboard discipline
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      const isMeta = e.metaKey || e.ctrlKey;
      const tag = (document.activeElement as HTMLElement)?.tagName ?? "";
      const isInput = ["INPUT", "TEXTAREA", "SELECT"].includes(tag);

      // Cmd+S — save only
      if (isMeta && e.key === "s") {
        e.preventDefault();
        questionCardRef.current?.triggerSave({ advance: false });
        return;
      }

      // Enter — save + advance (not from textarea)
      if (e.key === "Enter" && !isInput) {
        e.preventDefault();
        questionCardRef.current?.triggerSave({ advance: true });
        return;
      }

      // J / down — next question
      if ((e.key === "j" || e.key === "ArrowDown") && !isInput && !isMeta) {
        e.preventDefault();
        handleNext();
        return;
      }

      // K / up — prev question
      if ((e.key === "k" || e.key === "ArrowUp") && !isInput && !isMeta) {
        e.preventDefault();
        handlePrev();
        return;
      }

      // Cmd+1..0 — jump to CP1-CP10
      if (isMeta && e.key >= "1" && e.key <= "9") {
        e.preventDefault();
        selectCp(parseInt(e.key, 10));
        return;
      }
      if (isMeta && e.key === "0") {
        e.preventDefault();
        selectCp(10);
        return;
      }

      // Esc — (handled by child components via bubbling)
    }

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleNext, handlePrev, selectCp]);

  const compositeScore =
    account?.composite_score ?? account?.score ?? null;
  const nbq = nextBestQuestion();

  if (accountLoading || questionsLoading) {
    return (
      <OperatorShell
        breadcrumbs={[
          { label: "Accounts", href: "/accounts" },
          { label: "Answer Lane" },
        ]}
        showContextTabs={false}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 200,
            color: "#9CA3AF",
            fontSize: 14,
          }}
        >
          Loading...
        </div>
      </OperatorShell>
    );
  }

  if (!account) {
    return (
      <OperatorShell
        breadcrumbs={[
          { label: "Accounts", href: "/accounts" },
          { label: "Answer Lane" },
        ]}
        showContextTabs={false}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 200,
            color: "#DC2626",
            fontSize: 14,
          }}
        >
          Account not found.
        </div>
      </OperatorShell>
    );
  }

  const shellBreadcrumbs = [
    { label: "Accounts", href: "/accounts" },
    { label: account.name, href: `/account/${accountId}` },
    { label: "Answer Lane" },
  ];

  return (
    <OperatorShell
      breadcrumbs={shellBreadcrumbs}
      pageTitle="Answer Lane"
      pageMeta={account.name}
      showContextTabs={false}
    >
      {/* CP complete celebration banner */}
      {celebrationCp && (
        <div
          style={{
            position: "fixed",
            top: 72,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 200,
            background: "#0B0B0F",
            color: "#FFFFFF",
            padding: "10px 24px",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            boxShadow: "0 4px 20px rgba(0,0,0,0.18)",
            whiteSpace: "nowrap",
            animation: "fadeSlideDown 0.25s ease",
          }}
          role="status"
        >
          CP{celebrationCp} answers locked. Moving to CP{celebrationCp + 1}.
        </div>
      )}

      <div
        style={{
          display: "flex",
          height: "calc(100vh - 128px)",
          overflow: "hidden",
          background: "#FFFFFF",
        }}
      >
        {/* LEFT RAIL — desktop vertical, tablet/mobile horizontal top strip */}
        {isTablet || isMobile ? (
          <div
            style={{
              position: "absolute",
              top: 64,
              left: 0,
              right: 0,
              zIndex: 10,
            }}
          >
            <CpLeftRail
              cpProgress={cpProgress}
              activeCp={activeCp}
              onSelectCp={selectCp}
              horizontal
            />
          </div>
        ) : (
          <CpLeftRail
            cpProgress={cpProgress}
            activeCp={activeCp}
            onSelectCp={selectCp}
            accountName={account.name}
            compositeScore={compositeScore ?? undefined}
          />
        )}

        {/* CENTER COLUMN */}
        <main
          style={{
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            marginTop: isTablet || isMobile ? 48 : 0,
          }}
          aria-label="Question entry area"
        >
          {questions.length === 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                gap: 12,
                color: "#9CA3AF",
                fontSize: 14,
                padding: 32,
              }}
            >
              <p>No questions found for this account.</p>
              <p style={{ fontSize: 12 }}>
                Check that the account has questions assigned and you have access.
              </p>
            </div>
          ) : !activeQuestion ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                gap: 12,
                color: "#9CA3AF",
                fontSize: 14,
                padding: 32,
                textAlign: "center",
              }}
            >
              <p style={{ fontWeight: 700, color: "#16A34A", fontSize: 16 }}>
                All questions answered for this Checkpoint.
              </p>
              <p>Select another Checkpoint from the left rail to continue.</p>
            </div>
          ) : (
            <QuestionCard
              ref={questionCardRef}
              question={activeQuestion}
              allQuestionsInCp={questions.filter((q) => q.cp_number === activeCp)}
              cpNumber={activeCp}
              onSave={(payload, opts) =>
                saveAnswer(activeQuestion.question_key, payload, opts)
              }
              savingKey={savingKey}
              lastSavedAt={lastSavedAt}
              onAdvance={handleAdvance}
              onPrev={handlePrev}
              onNext={handleNext}
            />
          )}

          {/* CP navigation arrows */}
          {cpQuestions.length > 1 && activeQuestion && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 28px",
                borderTop: "1px solid #E5E5E7",
                background: "#FAFAF7",
              }}
            >
              <button
                type="button"
                onClick={handlePrev}
                disabled={activeIndex <= 0}
                style={{
                  padding: "6px 14px",
                  fontSize: 12,
                  fontWeight: 600,
                  color: activeIndex <= 0 ? "#D1D5DB" : "#0B0B0F",
                  background: "transparent",
                  border: `1px solid ${activeIndex <= 0 ? "#E5E5E7" : "#C9C9CC"}`,
                  borderRadius: 6,
                  cursor: activeIndex <= 0 ? "default" : "pointer",
                }}
              >
                Previous (K)
              </button>
              <span style={{ fontSize: 11, color: "#9CA3AF" }}>
                {activeIndex + 1} of {cpQuestions.length} in {CP_NAMES[activeCp]?.split(" — ")[0]}
              </span>
              <button
                type="button"
                onClick={handleNext}
                disabled={activeIndex >= cpQuestions.length - 1}
                style={{
                  padding: "6px 14px",
                  fontSize: 12,
                  fontWeight: 600,
                  color: activeIndex >= cpQuestions.length - 1 ? "#D1D5DB" : "#0B0B0F",
                  background: "transparent",
                  border: `1px solid ${activeIndex >= cpQuestions.length - 1 ? "#E5E5E7" : "#C9C9CC"}`,
                  borderRadius: 6,
                  cursor: activeIndex >= cpQuestions.length - 1 ? "default" : "pointer",
                }}
              >
                Next (J)
              </button>
            </div>
          )}
        </main>

        {/* RIGHT RAIL — desktop sidebar, mobile bottom sheet */}
        {isMobile ? (
          <ProgressRightRail
            compositeScore={compositeScore ?? null}
            totalAnswered={totalAnswered}
            totalQuestions={totalQuestions}
            accountName={account.name}
            cpProgress={cpProgress}
            nextBestQuestion={nbq}
            onJumpToQuestion={jumpToQuestion}
            bottomSheet
            bottomSheetOpen={bottomSheetOpen}
            onBottomSheetToggle={() => setBottomSheetOpen((v) => !v)}
          />
        ) : (
          <ProgressRightRail
            compositeScore={compositeScore ?? null}
            totalAnswered={totalAnswered}
            totalQuestions={totalQuestions}
            accountName={account.name}
            cpProgress={cpProgress}
            nextBestQuestion={nbq}
            onJumpToQuestion={jumpToQuestion}
          />
        )}
      </div>

      <style>{`
        @keyframes fadeSlideDown {
          from { opacity: 0; transform: translateX(-50%) translateY(-10px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </OperatorShell>
  );
}
